import { useState } from "react";
import { GripVertical, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { isRequired } from "@/lib/catalogue";

export const FIELD_TYPES = ["short text", "long text", "long text or file", "single-select", "multi-select", "number", "date"];
const SELECTS = ["single-select", "multi-select"];

const move = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
};

const RowShell = ({ idx, total, onMove, onDelete, dragging, setDragging, children, testid }) => (
  <li draggable onDragStart={() => setDragging(idx)} onDragEnd={() => setDragging(null)} onDragOver={(e) => e.preventDefault()}
    onDrop={() => { if (dragging !== null && dragging !== idx) onMove(dragging, idx); setDragging(null); }}
    className="flex items-start gap-2 rounded-xl border bg-card p-2" data-testid={testid}>
    <span className="mt-2 cursor-grab text-muted-foreground" aria-hidden="true"><GripVertical className="!h-4 !w-4" /></span>
    <div className="grid min-w-0 flex-1 gap-2">{children}</div>
    <div className="flex flex-col gap-1">
      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => onMove(idx, idx - 1)} aria-label="Move up"><ArrowUp className="!h-3.5 !w-3.5" /></Button>
      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === total - 1} onClick={() => onMove(idx, idx + 1)} aria-label="Move down"><ArrowDown className="!h-3.5 !w-3.5" /></Button>
      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDelete(idx)} aria-label="Delete row" data-testid={`${testid}-delete`}><Trash2 className="!h-3.5 !w-3.5" /></Button>
    </div>
  </li>
);

const JsonToggle = ({ value, onChange, testid }) => {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [err, setErr] = useState("");
  return (
    <div className="space-y-1">
      <Textarea rows={8} value={text} className="rounded-xl font-mono text-xs" aria-label="Edit as JSON" data-testid={`${testid}-json`}
        onChange={(e) => { setText(e.target.value); try { const v = JSON.parse(e.target.value); if (!Array.isArray(v)) throw new Error(); onChange(v); setErr(""); } catch { setErr("Not a valid JSON list yet."); } }} />
      {err && <p className="text-sm text-destructive">{err}</p>}
    </div>
  );
};

function RowsEditor({ rows, onChange, blank, render, label, testid }) {
  const [json, setJson] = useState(false);
  const [dragging, setDragging] = useState(null);
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <fieldset className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <legend className="text-sm font-medium">{label}</legend>
        <button type="button" className="text-sm underline underline-offset-4" onClick={() => setJson((j) => !j)} data-testid={`${testid}-json-toggle`}>{json ? "Use row editor" : "Edit as JSON"}</button>
      </div>
      {json ? <JsonToggle value={rows} onChange={onChange} testid={testid} /> : (
        <>
          <ul className="space-y-2">
            {rows.map((r, i) => (
              <RowShell key={i} idx={i} total={rows.length} dragging={dragging} setDragging={setDragging} onMove={(a, b) => onChange(move(rows, a, b))} onDelete={(x) => onChange(rows.filter((_, j) => j !== x))} testid={`${testid}-row-${i}`}>
                {render(r, (patch) => update(i, patch), i)}
              </RowShell>
            ))}
          </ul>
          <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...rows, blank])} data-testid={`${testid}-add`}><Plus className="!h-4 !w-4" /> Add row</Button>
        </>
      )}
    </fieldset>
  );
}

export const InputsEditor = ({ rows, onChange }) => (
  <RowsEditor rows={rows} onChange={onChange} label="What it needs" testid="inputs-editor" blank={{ input: "", required: false, note: "" }}
    render={(r, set, i) => (
      <div className="grid gap-2 sm:grid-cols-[1fr_130px]">
        <Input value={r.input || ""} placeholder="Input" aria-label={`Input ${i + 1}`} onChange={(e) => set({ input: e.target.value })} />
        <select value={isRequired(r.required) ? "required" : "optional"} aria-label={`Input ${i + 1} required or optional`} onChange={(e) => set({ required: e.target.value === "required" })} className="h-10 rounded-xl border border-input bg-card px-2 text-sm">
          <option value="required">Required</option><option value="optional">Optional</option>
        </select>
        <Input className="sm:col-span-2" value={r.note || ""} placeholder="Note" aria-label={`Input ${i + 1} note`} onChange={(e) => set({ note: e.target.value })} />
      </div>
    )} />
);

export const FieldsEditor = ({ rows, onChange, label = "Form fields", testid = "fields-editor" }) => (
  <RowsEditor rows={rows} onChange={onChange} label={label} testid={testid} blank={{ name: "", type: "short text", options: "", mandatory: false, maps_to: "" }}
    render={(r, set, i) => {
      const opts = Array.isArray(r.options) ? r.options.join("; ") : r.options || "";
      const types = FIELD_TYPES.includes(r.type) || !r.type ? FIELD_TYPES : [r.type, ...FIELD_TYPES];
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input value={r.name || ""} placeholder="Field name" aria-label={`Field ${i + 1} name`} onChange={(e) => set({ name: e.target.value })} />
          <select value={r.type || "short text"} aria-label={`Field ${i + 1} type`} onChange={(e) => set({ type: e.target.value })} className="h-10 rounded-xl border border-input bg-card px-2 text-sm">
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input value={opts} placeholder={SELECTS.includes(r.type) ? "Options, separated by semicolons" : "Options (for selects)"} aria-label={`Field ${i + 1} options`} onChange={(e) => set({ options: e.target.value })} />
          <Input value={r.maps_to || ""} placeholder="Maps to" aria-label={`Field ${i + 1} maps to`} onChange={(e) => set({ maps_to: e.target.value })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={r.mandatory === true || String(r.mandatory).toLowerCase() === "true"} onChange={(e) => set({ mandatory: e.target.checked })} /> Mandatory</label>
        </div>
      );
    }} />
);

export function validateRows(key, rows) {
  if (key === "inputs_summary") {
    if (rows.some((r) => !String(r.input || "").trim())) return "Every row in What it needs needs an input name.";
    return "";
  }
  const names = rows.map((r) => String(r.name || "").trim());
  if (names.some((n) => !n)) return "Every form field needs a name.";
  if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) return "Two form fields have the same name.";
  const bad = rows.find((r) => SELECTS.includes(r.type) && !String(Array.isArray(r.options) ? r.options.join("") : r.options || "").trim());
  if (bad) return `${bad.name} is a select field, so it needs options.`;
  return "";
}

export const normaliseFields = (rows) => rows.map((r) => ({
  name: String(r.name || "").trim(), type: r.type || "", mandatory: r.mandatory === true || String(r.mandatory).toLowerCase() === "true",
  maps_to: r.maps_to || null,
  options: Array.isArray(r.options) ? r.options : String(r.options || "").split(";").map((s) => s.trim()).filter(Boolean),
}));
